export class ApiError extends Error {
  constructor(
    public status: number,
    public code: string,
    message: string,
    public details?: unknown,
  ) {
    super(message);
  }
}

export type Params = Record<string, string | number | boolean | null | undefined>;

export function qs(params?: Params): string {
  if (!params) return '';
  const sp = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || v === null || v === '') continue;
    sp.set(k, String(v));
  }
  const s = sp.toString();
  return s ? `?${s}` : '';
}

let onUnauthorized: (() => void) | undefined;
/** Called when a request other than login comes back 401 (session expired or revoked). */
export function setUnauthorizedHandler(fn: (() => void) | undefined) {
  onUnauthorized = fn;
}

async function request<T>(method: string, path: string, opts: { body?: unknown; params?: Params } = {}): Promise<T> {
  const res = await fetch(`/api${path}${qs(opts.params)}`, {
    method,
    credentials: 'same-origin',
    headers: opts.body !== undefined ? { 'Content-Type': 'application/json' } : undefined,
    body: opts.body !== undefined ? JSON.stringify(opts.body) : undefined,
  });
  if (res.status === 204) return undefined as T;

  const text = await res.text();
  let data: unknown;
  try {
    data = text ? JSON.parse(text) : undefined;
  } catch {
    data = undefined;
  }
  if (!res.ok) {
    const err = (data as { error?: { code?: string; message?: string; details?: unknown } } | undefined)?.error;
    if (res.status === 401 && !path.startsWith('/auth/login')) onUnauthorized?.();
    throw new ApiError(res.status, err?.code ?? 'error', err?.message ?? (res.statusText || 'Request failed'), err?.details);
  }
  return data as T;
}

export const api = {
  get: <T>(path: string, params?: Params) => request<T>('GET', path, { params }),
  post: <T>(path: string, body?: unknown) => request<T>('POST', path, { body: body ?? {} }),
  put: <T>(path: string, body: unknown) => request<T>('PUT', path, { body }),
  patch: <T>(path: string, body: unknown) => request<T>('PATCH', path, { body }),
  delete: <T = void>(path: string) => request<T>('DELETE', path),
};

/** Field-level messages from a 400 validation response: [{ path: 'items.0.amount', message }]. */
export function fieldErrors(err: unknown): { path: string; message: string }[] {
  if (!(err instanceof ApiError)) return [];
  const d = err.details;
  if (Array.isArray(d)) {
    return d.filter((x): x is { path: string; message: string } => typeof x?.path === 'string' && typeof x?.message === 'string');
  }
  const field = (d as { field?: unknown } | undefined)?.field;
  return typeof field === 'string' ? [{ path: field, message: err.message }] : [];
}
